// One RGBA8 texture, one texel per hex, shared by the lens and fog layers so the terrain shader
// spends a single texture binding on both (the terrain material is near the per-stage limit).
//
//   texel.r ... lens byte           (lens.ts:  lensBytes)
//   texel.g ... fog field, current  (fog.ts:   buildFogField, 0 fresh .. 255 no recent source)
//   texel.b ... fog field, previous (the field the reveal animation folds back from)
//   texel.a ... city mark           (cityMarks.ts: 0 none, city, capital)
//
// One extra row below the hexes holds the lens colour table (lensLut.ts): entry i at column i.
// A second block of hex rows follows it, one byte per hex in the red channel (claimMarks.ts): 255
// where the leader's point of view claims the hex from another state, for the strategic hatching.

import * as THREE from 'three/webgpu';
import { COLS, HEX_COUNT, ROWS } from '../../core/grid.ts';

export const CHANNEL = { LENS: 0, FOG: 1, FOG_PREV: 2, CITY: 3 } as const;
export type Channel = (typeof CHANNEL)[keyof typeof CHANNEL];

const CHANNELS = 4;
/** Texel row of the lens colour table. */
export const LUT_ROW = ROWS;
/** First texel row of the claim block, after the lens colour table. */
export const CLAIM_ROW = ROWS + 1;
const CLAIM_TEXEL = HEX_COUNT + COLS;

export interface OverlayData {
  texture: THREE.DataTexture;
  /** Copy one byte per hex into a channel and re-upload. */
  write(channel: Channel, bytes: Uint8Array): void;
  /** Store the lens colour table (RGBA bytes, one entry per column of the table row). */
  writeTable(rgba: Uint8Array): void;
  /** Copy one byte per hex into the claim block (red channel) and re-upload. */
  writeClaims(bytes: Uint8Array): void;
  dispose(): void;
}

export function createOverlayData(): OverlayData {
  const texels = new Uint8Array((CLAIM_TEXEL + HEX_COUNT) * CHANNELS);
  const texture = new THREE.DataTexture(texels, COLS, CLAIM_ROW + ROWS, THREE.RGBAFormat, THREE.UnsignedByteType);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;

  return {
    texture,
    write(channel, bytes) {
      for (let i = 0; i < HEX_COUNT; i++) {
        texels[i * CHANNELS + channel] = bytes[i];
      }
      texture.needsUpdate = true;
    },
    writeTable(rgba) {
      texels.set(rgba, HEX_COUNT * CHANNELS);
      texture.needsUpdate = true;
    },
    writeClaims(bytes) {
      for (let i = 0; i < HEX_COUNT; i++) {
        texels[(CLAIM_TEXEL + i) * CHANNELS] = bytes[i];
      }
      texture.needsUpdate = true;
    },
    dispose() {
      texture.dispose();
    },
  };
}
